import React from "react";
import {
  Typography,
  Box,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";
import {
  IOption,
  LabelDropDown,
  GenericDropdown,
  Icon,
  IconName,
  SanitizedHtml,
} from "halifax";
import "./styles.scss";
import clsx from "clsx";
import { IFlightNumberFilter } from "redmond";

import * as textConstants from "./textConstants";
import {
  IAirlineOptions,
  IFlightNumbersByAirlineCode,
} from "../../../../reducer";
import { generateFilterTrackingEvent } from "../../../../../search/actions/actions";
import { trackEvent } from "../../../../../../api/v0/trackEvent";

interface IFlightNumberFilterProps {
  allAirlines: IAirlineOptions[];
  flightNumberFilter: IFlightNumberFilter[];
  flightNumbersByAirline: IFlightNumbersByAirlineCode;
  departureDateString: string;
  setFlightNumberFilter: (flightNumbers: IFlightNumberFilter[]) => void;
  flightNumberAirlineFilter: string[];
  setFlightNumberAirlineFilter: (airlines: string[]) => void;
  showDropdownContentOnly?: boolean;
  icon?: IconName;
  title?: string;
  useAirlineCheckbox?: boolean;
  airlineSubtitle?: string;
  flightNumberSubtitle?: string;
}

export const FlightNumberFilterSelection = (
  props: IFlightNumberFilterProps,
) => {
  const {
    allAirlines,
    flightNumbersByAirline,
    setFlightNumberFilter,
    flightNumberFilter = [],
    departureDateString,
    showDropdownContentOnly,
    icon,
    title,
    useAirlineCheckbox,
    airlineSubtitle,
    flightNumberSubtitle,
    flightNumberAirlineFilter,
    setFlightNumberAirlineFilter,
  } = props;
  const toOptions = (values: string[], labelPrefix?: string): IOption[] => {
    return values.reduce((result: IOption[], value) => {
      const label = labelPrefix ? `${labelPrefix} ${value}` : value;
      const option: IOption = { value, label };
      result.push(option);
      return result;
    }, []);
  };

  const handleAirlineCheckboxChange = (airlineValue: string) => {
    const isSelected = flightNumberAirlineFilter.includes(airlineValue);
    if (isSelected) {
      setFlightNumberAirlineFilter(
        flightNumberAirlineFilter.filter((a) => a !== airlineValue),
      );
      setFlightNumberFilter(
        flightNumberFilter.filter((item) => item.airlineCode !== airlineValue),
      );
    } else {
      setFlightNumberAirlineFilter([
        ...flightNumberAirlineFilter,
        airlineValue,
      ]);
    }
  };

  const handleAirlineDropdownChange = (airlineValue: string) => {
    if (
      flightNumberAirlineFilter.length === 1 &&
      flightNumberAirlineFilter[0] === airlineValue
    ) {
      setFlightNumberAirlineFilter([]);
    } else {
      setFlightNumberAirlineFilter([airlineValue]);
    }
    setFlightNumberFilter([]);
  };

  const handleFlightNumberSelection = (
    airlineCode: string,
    newValue: string,
  ) => {
    const filterValue: IFlightNumberFilter = {
      airlineCode,
      flightNumber: newValue,
    };

    const shouldRemoveFlight = flightNumberFilter.some(
      (item) =>
        item.airlineCode === filterValue.airlineCode &&
        item.flightNumber === filterValue.flightNumber,
    );

    if (shouldRemoveFlight) {
      const newFilters = flightNumberFilter.filter(
        (item) =>
          item.airlineCode !== filterValue.airlineCode ||
          item.flightNumber !== filterValue.flightNumber,
      );
      setFlightNumberFilter(newFilters);
    } else {
      setFlightNumberFilter([...flightNumberFilter, filterValue]);
    }
    trackEvent(generateFilterTrackingEvent("flight number", "list"));
  };

  const isChecked = (airlineCode: string, flightNumber: string): boolean =>
    flightNumberFilter.some(
      (currentOption: IFlightNumberFilter) =>
        currentOption.flightNumber === flightNumber &&
        currentOption.airlineCode === airlineCode,
    );

  const renderFlightNumberOptions = (
    airlineCode: string,
    options: IOption[],
  ) => {
    const airlineOption = allAirlines.find(
      (airline) => airline.value === airlineCode,
    );
    return options.map((option: IOption) => (
      <FormControlLabel
        className="flight-number-options"
        key={`${airlineCode}-${option.value}`}
        control={
          <Checkbox
            className={clsx("flight-number-checkbox", {
              checked: isChecked(airlineCode, option.value),
            })}
            checked={isChecked(airlineCode, option.value)}
            onChange={() =>
              handleFlightNumberSelection(airlineCode, option.value)
            }
            value={option}
          />
        }
        labelPlacement="start"
        label={
          <Box className="label-wrapper">
            {airlineOption && airlineOption.icon && airlineOption.icon}
            <Typography>{option.label}</Typography>
          </Box>
        }
      />
    ));
  };

  const renderAirlineAsCheckbox = (airlines: IAirlineOptions[]) =>
    airlines.map((airline: IAirlineOptions) => (
      <FormControlLabel
        className="airline-options"
        key={airline.value}
        control={
          <Checkbox
            className="airline-checkbox"
            checked={flightNumberAirlineFilter.includes(airline.value)}
            onChange={(newValue) =>
              handleAirlineCheckboxChange(newValue.target.value)
            }
            value={airline.value}
          />
        }
        labelPlacement="start"
        label={
          <Box className="label-wrapper">
            {airline.icon && airline.icon}
            <Typography>{airline.label}</Typography>
          </Box>
        }
      />
    ));

  const renderDropdownContent = () => (
    <Box className="flight-number-selection-root">
      <Box className="flight-number-selection-container">
        {!!title && showDropdownContentOnly ? (
          <Box className="header-container">
            {icon && <Icon name={icon} />} {title}
          </Box>
        ) : (
          <Box className="header-container">
            <Typography variant="body1" className="header-text">
              {textConstants.HEADER(departureDateString)}
            </Typography>
          </Box>
        )}
        <Box className="airline-and-flight-number-selection-wrapper">
          <Box className="airline-selection">
            {airlineSubtitle && (
              <SanitizedHtml
                as={Typography}
                className="airline-selection-subtitle"
                html={airlineSubtitle}
              />
            )}
            <Box>
              {useAirlineCheckbox ? (
                <FormControl component="fieldset">
                  <FormGroup>{renderAirlineAsCheckbox(allAirlines)} </FormGroup>
                </FormControl>
              ) : (
                <LabelDropDown
                  classes={["airline-selection-menu"]}
                  options={allAirlines}
                  label=""
                  ariaLabel="Airline Selector"
                  onChange={(newValue) => handleAirlineDropdownChange(newValue)}
                  value={flightNumberAirlineFilter[0] ?? ""}
                />
              )}
            </Box>
          </Box>
          {flightNumberAirlineFilter.length > 0 && (
            <Box className="flight-number-selection">
              {flightNumberSubtitle && (
                <SanitizedHtml
                  as={Typography}
                  className="flight-number-selection-subtitle"
                  html={flightNumberSubtitle}
                />
              )}
              <FormControl component="fieldset">
                <FormGroup>
                  {flightNumberAirlineFilter.flatMap((airlineCode) =>
                    renderFlightNumberOptions(
                      airlineCode,
                      toOptions(
                        flightNumbersByAirline[airlineCode] ?? [],
                        airlineCode,
                      ),
                    ),
                  )}
                </FormGroup>
              </FormControl>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box className="flight-number-selection-dropdown">
      {!showDropdownContentOnly && (
        <GenericDropdown
          buttonClassName="b2b-shop-filter"
          popoverClassName={clsx("flight-number-selection-popover", "b2b")}
          ariaLabel="Flight Number Filter"
          dropdownLabel={textConstants.FLIGHT_NUMBER}
          dropdownContent={renderDropdownContent()}
        />
      )}
      {!!showDropdownContentOnly && renderDropdownContent()}
    </Box>
  );
};
